import { useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { MuiTelInput } from "mui-tel-input";

import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import IndustrySelect from "../../IndustrySelect";

import { testEmailValidation } from "../../../helpers";
import { beaconContactRequest } from "../../../services/beaconService";
import { useLocalizedTranslation } from "../../../hooks/useLocalizedTranslation";

const formInitialValues = {
  name: "",
  email: "",
  mobile: "",
  jobAtCompany: "",
  industry: "",
  message: "",
};

const validateForm = (values, translation) => {
  const errors = {};
  const { name, email, mobile } = values;

  !name && (errors.name = translation("requiredField"));
  !mobile && (errors.mobile = translation("requiredField"));

  if (email) {
    if (!testEmailValidation(email)) {
      errors.email = translation("emailInvalid");
    }
  }

  return errors;
};

const VisitorInfoModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  beaconCode = "",
  language = null,
}) => {
  const { t: translation } = useLocalizedTranslation(
    language,
    "components/visitorInfoModal"
  );
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleChange,
    values,
    touched,
    errors,
    handleSubmit,
    setFieldValue,
    handleBlur,
    resetForm,
  } = useFormik({
    initialValues: formInitialValues,
    validate: (values) => validateForm(values, translation),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        const { name, mobile, email, jobAtCompany, industry, message } = values;

        const response = await beaconContactRequest(
          beaconCode,
          name,
          mobile,
          email.trim(),
          jobAtCompany,
          industry,
          message
        );

        if (response?.acknowledged) {
          closeModal();
        }
      } catch (error) {
        toast(error.message, { type: "error" });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleMobileChange = (value) => {
    setFieldValue("mobile", value);
  };

  const closeModal = () => {
    resetForm();

    setIsModalOpen(false);
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={closeModal}
      fullWidth
      sx={{ direction: language && language === "ar" ? "rtl" : "ltr" }}
    >
      <DialogTitle>
        <Tooltip title={translation("closeIconTooltipTitle")}>
          <IconButton
            aria-label="cancel"
            onClick={closeModal}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent>
        <Box>
          <Typography textAlign="center" component="h1" variant="h5" mb={4}>
            {translation("title")}
          </Typography>

          <Box marginBottom={2}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              placeholder={translation("nameLabel") + " *"}
            />
          </Box>

          <Box marginBottom={2}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              name="email"
              value={values.email.trim()}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              placeholder={translation("emailLabel")}
            />
          </Box>

          <Box marginBottom={2}>
            <MuiTelInput
              margin="normal"
              required
              fullWidth
              id="mobile"
              name="mobile"
              value={values.mobile}
              onChange={handleMobileChange}
              onBlur={handleBlur}
              error={touched.mobile && Boolean(errors.mobile)}
              helperText={touched.mobile && errors.mobile}
              placeholder={translation("mobileLabel") + " *"}
              defaultCountry="LB"
            />
          </Box>

          <Box marginBottom={2}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="jobAtCompany"
              name="jobAtCompany"
              value={values.jobAtCompany}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.jobAtCompany && Boolean(errors.jobAtCompany)}
              helperText={touched.jobAtCompany && errors.jobAtCompany}
              placeholder={translation("jobAtCompanyLabel")}
            />
          </Box>

          <Box marginBottom={2}>
            <IndustrySelect
              name="industry"
              placeholder={translation("industryLabel")}
              value={values.industry}
              onChange={handleChange}
              language={language}
            />
          </Box>

          <Box marginBottom={4}>
            <TextField
              margin="normal"
              fullWidth
              id="message"
              name="message"
              value={values.message}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.message && Boolean(errors.message)}
              helperText={touched.message && errors.message}
              placeholder={translation("messageLabel")}
              multiline
              rows={4}
            />
          </Box>

          {isLoading ? (
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={43} />
            </Box>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth
              size="large"
            >
              {translation("button")}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorInfoModal;
